// The six named problems the Problem Finder can land on. One per life area picked in
// question 1, so the offer a reader sees always matches the area they chose. The
// analysis writes the personal version of the problem; everything here is the fixed
// copy that sells the $19 plan and the app for that problem.

export const PROBLEM_IDS = [
  'work',
  'money',
  'energy',
  'relationships',
  'follow-through',
  'direction',
] as const;

export type ProblemId = (typeof PROBLEM_IDS)[number];

export interface ProblemDefinition {
  id: ProblemId;
  /** The question 1 answer that routes a reader to this problem. Must match questions.json. */
  area: string;
  /** Short label for chips, analytics and the goal the app creates. */
  label: string;
  /** Product name of the $19 plan, shown on the offer and the checkout. */
  planName: string;
  /** Offer headline under the plan name. */
  offerHeadline: string;
  /** The goal title the app starts with, in the reader's voice. */
  goalTitle: string;
  /** The app pitch on the paid plan page. */
  appOffer: string;
  /** The one working tool the paid plan hands over, described for the plan writer. */
  planTool: string;
  /** Asked right after payment, before the plan is written. Every answer is optional. */
  intake: IntakeQuestion[];
}

export interface IntakeQuestion {
  id: string;
  question: string;
  placeholder: string;
}

export const PROBLEMS: Record<ProblemId, ProblemDefinition> = {
  work: {
    id: 'work',
    area: 'Work',
    label: 'Work no longer fits',
    planName: '14-Day Pivot Plan',
    offerHeadline: 'Turn "I need a change" into 14 small steps',
    goalTitle: "Make my work fit who I'm becoming",
    appOffer: 'Work through your pivot with your coach',
    planTool:
      'A conversation script for talking to someone already doing work you are curious about: how to ask for 15 minutes, and the five questions to ask.',
    intake: [
      { id: 'now', question: 'What is your work right now, and which part drains you most?', placeholder: 'e.g. Project manager at a logistics firm. The Monday status meetings.' },
      { id: 'pull', question: 'If you changed direction, what is pulling at you, even vaguely?', placeholder: 'e.g. Something with teaching, or working with my hands.' },
      { id: 'fixed', question: "What can't change in the next few months?", placeholder: 'e.g. I need the same income until my daughter finishes school in June.' },
    ],
  },
  money: {
    id: 'money',
    area: 'Money',
    label: 'Money stress',
    planName: '14-Day Breathing Room Plan',
    offerHeadline: 'Two weeks of breathing room, one money move a day',
    goalTitle: 'Stop money stress running my week',
    appOffer: 'Get money off your mind with your coach',
    planTool:
      "A one-page money sheet: every income source, the essential monthly costs, the weekly number that keeps them covered, and this week's one money move.",
    intake: [
      { id: 'numbers', question: 'What is the money problem in numbers, as best you know?', placeholder: 'e.g. About $600 short each month since my hours were cut.' },
      { id: 'worry', question: 'Which bill or number worries you most?', placeholder: 'e.g. The credit card at 24%, around $8,000.' },
      { id: 'tried', question: 'What have you already cut or tried?', placeholder: 'e.g. Cancelled subscriptions, stopped eating out. Still short.' },
    ],
  },
  energy: {
    id: 'energy',
    area: 'Sleep and energy',
    label: 'Sleep and energy',
    planName: '14-Day Energy Reset',
    offerHeadline: 'Get your evenings and mornings back in 14 days',
    goalTitle: 'Get my sleep and energy back',
    appOffer: 'Rebuild your energy with your coach',
    planTool:
      'A sleep and energy log: bedtime, wake time, energy at 3pm, and what was different that day, for 14 days, with the one change being tested.',
    intake: [
      { id: 'nights', question: 'What does a normal night and morning look like right now?', placeholder: 'e.g. Bed around midnight, awake at 3, up at 6:30 feeling wrecked.' },
      { id: 'crash', question: 'When in the day do you run out?', placeholder: 'e.g. By 2pm I need coffee just to get through meetings.' },
      { id: 'blocker', question: 'What gets in the way of fixing it?', placeholder: 'e.g. Late calls with the US team, and my phone in bed.' },
    ],
  },
  relationships: {
    id: 'relationships',
    area: 'The people closest to me',
    label: 'Drifting from people',
    planName: '14-Day Reconnect Plan',
    offerHeadline: 'Fourteen small ways back to the people closest to you',
    goalTitle: 'Reconnect with the people who matter most',
    appOffer: 'Reconnect with your coach checking in',
    planTool:
      'Reach-out messages: short, low-pressure ways to restart contact with someone they have drifted from, by text, by call and in person.',
    intake: [
      { id: 'who', question: 'Who have you drifted from?', placeholder: 'e.g. My brother, and two friends from university.' },
      { id: 'what', question: 'What happened, or what keeps it from getting better?', placeholder: 'e.g. Nothing dramatic. We stopped calling after I moved.' },
      { id: 'better', question: "What would 'better' look like a month from now?", placeholder: 'e.g. A phone call every couple of weeks without it feeling awkward.' },
    ],
  },
  'follow-through': {
    id: 'follow-through',
    area: 'Sticking with things',
    label: 'Plans that fall apart',
    planName: '14-Day Follow-Through Plan',
    offerHeadline: 'A plan built for the week it falls apart',
    goalTitle: 'Stick with what matters, even in a bad week',
    appOffer: 'Keep going with a coach who adjusts when life gets in the way',
    planTool:
      'A bad-week card: the smallest version of each step, the cue that starts it, and the rule for restarting after a missed day.',
    intake: [
      { id: 'thing', question: 'What is the thing you keep starting and dropping?', placeholder: 'e.g. Running. Three weeks on, then a work trip, then nothing.' },
      { id: 'breaks', question: 'What usually happens on the day it falls apart?', placeholder: 'e.g. I miss one morning and decide the week is ruined.' },
      { id: 'week', question: 'What does your week actually look like?', placeholder: "e.g. Long days Monday to Thursday, kids' sport at the weekend." },
    ],
  },
  direction: {
    id: 'direction',
    area: 'Where my life is going',
    label: 'No clear direction',
    planName: '14-Day Direction Plan',
    offerHeadline: 'Find your next direction in 14 days, one question a day',
    goalTitle: 'Find where my life goes next',
    appOffer: 'Test your next direction with your coach',
    planTool:
      'A direction test sheet: each direction being considered, the smallest real test of it, when it runs, and what the test showed.',
    intake: [
      { id: 'changed', question: 'What has changed that makes you ask where life is going?', placeholder: "e.g. The kids left home and my job feels like it's on autopilot." },
      { id: 'options', question: 'Which directions are you even half considering?', placeholder: 'e.g. Going part-time, retraining as a counsellor, or moving closer to family.' },
      { id: 'matters', question: 'What matters most in whatever comes next?', placeholder: 'e.g. Feeling useful, and more time outdoors.' },
    ],
  },
};

export function isProblemId(value: unknown): value is ProblemId {
  return typeof value === 'string' && (PROBLEM_IDS as readonly string[]).includes(value);
}

/** The problem for a question 1 answer, or null for an answer the assessment never offered. */
export function problemForArea(area: string): ProblemDefinition | null {
  return Object.values(PROBLEMS).find((problem) => problem.area === area) ?? null;
}

/** "Question: answer" lines for the plan writer, in the order the questions were asked. */
export function formatIntake(problem: ProblemDefinition, answers: Record<string, string>): string | null {
  const lines = problem.intake
    .filter((q) => answers[q.id]?.trim())
    .map((q) => `${q.question} ${answers[q.id].trim()}`);
  return lines.length > 0 ? lines.join('\n') : null;
}
